import {
  PinwheelExperienceInput,
  VaroAdvanceEligibilityNotQualified,
} from '@varo/graphql-schema-types';
import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useVaroAdvanceEligibility from 'features/advance/hooks/useAdvanceEligibility';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const HowToQualifyVaroAdvance = () => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'whatIsVaStrings',
  });

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    isLoading,
    errorModel: errorModelForDD,
    refetchDdManagementFlow,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow();

  const { data, errorModel, fetching } = useVaroAdvanceEligibility();

  const dismissToTab = useDismissToRootTab();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-advance/qualification',
  });

  const pinwheelAdvanceLaunched = usePressableAnalytics({
    analyticsId: 'directDepositSwitchInitiatedAdvance',
  });

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  const varoAdvanceEligibility =
    data?.varoAdvanceEligibility as VaroAdvanceEligibilityNotQualified;

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const handleDirectDeposit = () => {
    if (shouldRedirectToDDManagementPage) {
      return router.push(
        '/move-money/direct-deposit/direct-deposit-management',
      );
    }
    const outcomeCallback = shouldLaunchPinwheelSDK
      ? refetchDdManagementFlow
      : goHome;

    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.DirectDepositSwitch,
        skipIntroScreen,
      },
      launchBehavior: skipIntroScreen ? { delayAfterTokenMs: 3000 } : undefined,
      onOutcome: outcomeCallback,
      ...(shouldLaunchPinwheelSDK && {
        onLinkTokenError: handleDdManagementLinkTokenFailure,
      }),
    });

    pinwheelAdvanceLaunched();
  };

  const getErrorModel = () => {
    const model = errorModelForDD ?? errorModel;
    return model ? <ErrorScreen {...model} /> : null;
  };

  const contentBody = (
    <View style={[styles.contentContainer, styles.bodyContentStyle]}>
      <BodyBaseText numberOfLines={0}>
        <ContentList
          contentList={varoAdvanceEligibility?.qualificationContentList ?? []}
        />
      </BodyBaseText>
      <BodyXSText numberOfLines={0}>
        {translatedText('termsAndConditions')}
      </BodyXSText>
    </View>
  );

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          gestureEnabled: false,
          title: translatedText('title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} transparent primaryOnPress={goHome} />
          ),
        }}
      />
      <ProductIntroScreen
        headlineText={varoAdvanceEligibility?.title}
        headlineTextStyle="expressive"
        backgroundTexture={backgroundTextureLight as ImageSourcePropType}
        primaryButton={{
          label: translatedText(
            shouldRedirectToDDManagementPage
              ? 'manageDirectDepositButton'
              : 'directDepositButton',
          ),
          analyticsId: 'setupDirectDepositClicked',
          onPress: handleDirectDeposit,
        }}
        bodyContent={contentBody}
        visual="ADVANCE_A"
        topPadding={80}
      />
      <ScreenTransitionOverlay
        isLoading={fetching || isPinwheelLoading || isLoading}
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={isLoading || fetching}
            isPinwheelLoading={isPinwheelLoading}
            defaultLoadingComponent={
              <View style={{ paddingTop: insets.top }}>
                <LoadingSpinner />
              </View>
            }
          />
        }
        ErrorComponent={getErrorModel()}
      />
    </View>
  );
};

export const styles = StyleSheet.create({
  bodyContentStyle: {
    backgroundColor: theme.color.background.primary,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
});

export default HowToQualifyVaroAdvance;
